/**
 * CartModal
 * 
 * Modal for managing cart items and batch checkout.
 * Displays all classes in cart, calculates total, and handles
 * batch signup submission.
 */

import React, { useState, useEffect } from 'react';
import { Trash2, DollarSign, ShoppingCart, CheckCircle, XCircle, AlertCircle, Loader2 } from 'lucide-react';
import { Modal } from '../common';
import { useCart } from '../../context';
import { UserInfo } from '../../types';
import { formatTime, getBatchVenmoUrl, isValidEmail } from '../../helpers/formatUtils';
import { createSignup, SignupResult } from '../../services/signupService';
import { Firestore } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { User } from 'firebase/auth';

interface CartModalProps {
  isOpen: boolean;
  onClose: () => void;
  venmoUsername: string;
  db: Firestore;
  appId: string;
  user?: User | null;
  isGoogleUser?: boolean;
  onSignInWithGoogle?: () => Promise<void>;
}

interface SignupResultWithClass extends SignupResult {
  className: string;
  classDate: string;
}

const CartModal: React.FC<CartModalProps> = ({
  isOpen,
  onClose,
  venmoUsername,
  db,
  appId,
  user,
  isGoogleUser = false,
  onSignInWithGoogle
}) => {
  const { cartItems, cartTotal, cartCount, removeFromCart, clearCart } = useCart();
  
  const [userInfo, setUserInfo] = useState<UserInfo>({
    firstName: '',
    lastName: '',
    email: ''
  });
  // Single payment method selection for the whole cart
  const [paymentMethod, setPaymentMethod] = useState<'venmo' | 'arrangement'>('venmo');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [results, setResults] = useState<SignupResultWithClass[]>([]);
  const [showResults, setShowResults] = useState(false);

  // Auto-fill user info from Google sign-in
  useEffect(() => {
    if (isGoogleUser && user) {
      const names = (user.displayName || '').split(' ');
      const firstName = names[0] || '';
      const lastName = names.slice(1).join(' ') || '';
      const email = user.email || '';
      
      setUserInfo(prev => ({
        firstName: prev.firstName || firstName,
        lastName: prev.lastName || lastName,
        email: prev.email || email
      }));
    }
  }, [isGoogleUser, user]);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setPaymentMethod('venmo');
      setShowResults(false);
      setResults([]);
    }
  }, [isOpen]);

  const hasPaidClasses = cartItems.some(item => item.paymentType === 'venmo');
  // Check if any paid classes allow alternate arrangements
  const hasArrangementsOption = cartItems.some(
    item => item.paymentType === 'venmo' && item.allowArrangements
  );

  const handleGoogleSignIn = async () => {
    if (onSignInWithGoogle) {
      await onSignInWithGoogle();
    }
  };

  /**
   * Submit all cart items as signups.
   * Processes each class and collects results.
   * Returns true if all signups succeeded.
   */
  const handleBatchSignup = async (): Promise<boolean> => {
    if (!isValidEmail(userInfo.email)) return false;

    setIsSubmitting(true);
    const signupResults: SignupResultWithClass[] = [];

    for (const classItem of cartItems) {
      try {
        // Determine payment method based on class type and user selection
        let paymentMethodValue: 'venmo' | 'arrangement' | 'none' = 'none';
        let paymentStatus: 'pending' | 'completed' | 'venmo' = 'completed';
        
        if (classItem.paymentType === 'venmo') {
          if (paymentMethod === 'arrangement' && classItem.allowArrangements) {
            paymentMethodValue = 'arrangement';
            paymentStatus = 'pending';
          } else {
            paymentMethodValue = 'venmo';
            // Use 'venmo' status - payment happens after registration
            paymentStatus = 'venmo';
          }
        }

        const result = await createSignup(db, appId, {
          classId: classItem.id,
          firstName: userInfo.firstName,
          lastName: userInfo.lastName,
          email: userInfo.email,
          paymentMethod: paymentMethodValue,
          paymentStatus,
          status: 'active',
          isBatchSignup: true  // Flag to skip individual emails
        });

        signupResults.push({
          ...result,
          className: classItem.name,
          classDate: classItem.date
        });
      } catch (error) {
        console.error(`Failed to signup for ${classItem.name}:`, error);
        signupResults.push({
          success: false,
          error: 'UNKNOWN',
          message: 'An unexpected error occurred.',
          className: classItem.name,
          classDate: classItem.date
        });
      }
    }

    setResults(signupResults);
    setShowResults(true);
    setIsSubmitting(false);

    // If all succeeded, clear cart immediately then send confirmation email
    const allSucceeded = signupResults.every(r => r.success);
    if (allSucceeded) {
      // Capture cart items BEFORE clearing (needed for email)
      const itemsForEmail = [...cartItems];
      
      // Clear cart IMMEDIATELY so badge updates instantly
      clearCart();
      
      // Send batch confirmation email asynchronously (don't block UI)
      // Note: Functions are deployed to us-east4 region
      const functions = getFunctions(undefined, 'us-east4');
      const sendBatchConfirmation = httpsCallable(functions, 'sendBatchConfirmation');
      sendBatchConfirmation({
        appId,
        userInfo: {
          firstName: userInfo.firstName,
          lastName: userInfo.lastName,
          email: userInfo.email
        },
        registrations: itemsForEmail.map(classItem => ({
          classId: classItem.id,
          paymentMethod: classItem.paymentType === 'venmo' 
            ? (paymentMethod === 'arrangement' && classItem.allowArrangements ? 'arrangement' : 'venmo')
            : 'none'
        }))
      }).then(() => {
        console.log('Batch confirmation emails sent');
      }).catch((emailError) => {
        console.error('Failed to send batch confirmation emails:', emailError);
        // Don't fail the signup if emails fail
      });
      
      return true;
    }
    return false;
  };

  const handleClose = () => {
    setShowResults(false);
    setResults([]);
    onClose();
  };

  const successCount = results.filter(r => r.success).length;
  const failureCount = results.filter(r => !r.success).length;

  // Loading view while submitting
  if (isSubmitting) {
    return (
      <Modal isOpen={isOpen} onClose={() => {}} title="Registering...">
        <div className="text-center py-12">
          <Loader2 className="mx-auto text-fuchsia-400 mb-4 animate-spin" size={48} />
          <p className="text-lg text-slate-200 font-medium">Registering for {cartCount} class{cartCount > 1 ? 'es' : ''}...</p>
          <p className="text-sm text-slate-400 mt-2">Please wait while we confirm your bookings.</p>
        </div>
      </Modal>
    );
  }

  // Results view after submission
  if (showResults) {
    return (
      <Modal isOpen={isOpen} onClose={handleClose} title="Booking Results">
        <div className="space-y-4">
          {/* Summary */}
          <div className={`p-4 rounded-lg ${failureCount === 0 ? 'bg-emerald-900/30 border border-emerald-700' : 'bg-amber-900/30 border border-amber-700'}`}>
            {failureCount === 0 ? (
              <div className="flex items-center gap-2 text-emerald-400">
                <CheckCircle size={20} />
                <span className="font-bold">All {successCount} bookings confirmed!</span>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-amber-400">
                <AlertCircle size={20} />
                <span className="font-bold">{successCount} succeeded, {failureCount} failed</span>
              </div>
            )}
          </div>

          {/* Individual results */}
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {results.map((result, index) => (
              <div 
                key={index}
                className={`p-3 rounded border ${result.success ? 'bg-slate-800 border-slate-700' : 'bg-red-900/20 border-red-700'}`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-slate-200">{result.className}</p>
                    <p className="text-sm text-slate-400">{result.classDate}</p>
                  </div>
                  {result.success ? (
                    <CheckCircle className="text-emerald-400" size={20} />
                  ) : (
                    <XCircle className="text-red-400" size={20} />
                  )}
                </div>
                {!result.success && result.message && (
                  <p className="text-sm text-red-400 mt-1">{result.message}</p>
                )}
              </div>
            ))}
          </div>

          <button
            onClick={handleClose}
            className="w-full py-3 bg-gradient-to-r from-rose-600 to-fuchsia-600 text-white font-bold rounded hover:from-rose-500 hover:to-fuchsia-500"
          >
            Done
          </button>
        </div>
      </Modal>
    );
  }

  // Empty cart view
  if (cartCount === 0) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title="Your Cart">
        <div className="text-center py-8">
          <ShoppingCart className="mx-auto text-slate-500 mb-4" size={48} />
          <p className="text-slate-400">Your cart is empty</p>
          <p className="text-sm text-slate-500 mt-2">Add classes from the calendar to get started</p>
        </div>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Your Cart (${cartCount})`}>
      <div className="space-y-4">
        {/* Cart items list */}
        <div className="space-y-3 max-h-48 overflow-y-auto">
          {cartItems.map((classItem) => (
            <div 
              key={classItem.id}
              className="flex items-center justify-between p-3 bg-slate-800 rounded-lg border border-slate-700"
            >
              <div className="flex-1 min-w-0">
                <p className="font-medium text-slate-200 truncate">{classItem.name}</p>
                <p className="text-sm text-slate-400">
                  {classItem.date} @ {formatTime(classItem.startTime)}
                </p>
                {classItem.paymentType === 'venmo' && classItem.cost && (
                  <p className="text-sm text-emerald-400 font-medium">${classItem.cost}</p>
                )}
              </div>
              <button
                onClick={() => removeFromCart(classItem.id)}
                className="ml-2 p-2 text-slate-400 hover:text-red-400 transition-colors"
                title="Remove from cart"
              >
                <Trash2 size={18} />
              </button>
            </div>
          ))}
        </div>

        {/* Total */}
        {hasPaidClasses && (
          <div className="flex justify-between items-center p-3 bg-slate-800 rounded-lg border border-slate-700">
            <span className="font-bold text-slate-200">Total</span>
            <span className="font-bold text-emerald-400 text-lg">${cartTotal}</span>
          </div>
        )}

        {/* Divider */}
        <div className="border-t border-slate-700 pt-4">
          {/* Quick sign-in option for anonymous users */}
          {!isGoogleUser && onSignInWithGoogle && (
            <button
              type="button"
              onClick={handleGoogleSignIn}
              className="w-full py-2 px-4 mb-4 border border-slate-600 rounded flex items-center justify-center gap-2 text-slate-300 hover:bg-slate-800 transition-colors text-sm"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Sign in with Google to auto-fill
            </button>
          )}

          {/* User info form */}
          <div className="space-y-3">
            <input 
              className="w-full p-2 border border-slate-600 rounded text-slate-200 placeholder-slate-500 bg-slate-800" 
              placeholder="First Name" 
              value={userInfo.firstName} 
              onChange={e => setUserInfo({...userInfo, firstName: e.target.value})} 
            />
            <input 
              className="w-full p-2 border border-slate-600 rounded text-slate-200 placeholder-slate-500 bg-slate-800" 
              placeholder="Last Name" 
              value={userInfo.lastName} 
              onChange={e => setUserInfo({...userInfo, lastName: e.target.value})} 
            />
            <input 
              className="w-full p-2 border border-slate-600 rounded text-slate-200 placeholder-slate-500 bg-slate-800" 
              placeholder="Email" 
              type="email" 
              value={userInfo.email} 
              onChange={e => setUserInfo({...userInfo, email: e.target.value})} 
            />
          </div>
        </div>

        {/* Payment section for paid classes */}
        {hasPaidClasses && (
          <div className="bg-slate-800 p-4 rounded border border-slate-600 space-y-3">
            <p className="font-bold text-slate-300 flex items-center gap-2">
              <DollarSign size={16}/> Payment Method
            </p>
            
            {/* Payment Method Radio Group */}
            <div className="space-y-2">
              <label className="flex items-center gap-3 p-3 rounded border border-slate-600 cursor-pointer hover:bg-slate-700/50 transition-colors">
                <input
                  type="radio"
                  name="cartPaymentMethod"
                  value="venmo"
                  checked={paymentMethod === 'venmo'}
                  onChange={() => setPaymentMethod('venmo')}
                  className="w-4 h-4 text-blue-500 border-slate-500 focus:ring-blue-500"
                />
                <div className="flex items-center gap-2">
                  <DollarSign size={16} className="text-blue-400" />
                  <span className="text-slate-200">Pay with Venmo (${cartTotal})</span>
                </div>
              </label>
              
              {hasArrangementsOption && (
                <label className="flex items-center gap-3 p-3 rounded border border-slate-600 cursor-pointer hover:bg-slate-700/50 transition-colors">
                  <input
                    type="radio"
                    name="cartPaymentMethod"
                    value="arrangement"
                    checked={paymentMethod === 'arrangement'}
                    onChange={() => setPaymentMethod('arrangement')}
                    className="w-4 h-4 text-emerald-500 border-slate-500 focus:ring-emerald-500"
                  />
                  <span className="text-slate-200">I have made alternate arrangements</span>
                </label>
              )}
            </div>
          </div>
        )}

        {/* Submit button */}
        <button 
          onClick={async () => {
            const success = await handleBatchSignup();
            // Open Venmo AFTER successful registration if Venmo was selected
            if (success && paymentMethod === 'venmo' && hasPaidClasses) {
              window.open(getBatchVenmoUrl(cartTotal, cartCount, venmoUsername), '_blank');
            }
          }}
          disabled={isSubmitting || !userInfo.email || !isValidEmail(userInfo.email)} 
          className="w-full py-3 mt-2 bg-gradient-to-r from-rose-600 to-fuchsia-600 text-white font-bold rounded disabled:opacity-50 hover:from-rose-500 hover:to-fuchsia-500 shadow-lg"
        >
          {isSubmitting ? 'Registering...' : (
            hasPaidClasses && paymentMethod === 'venmo'
              ? `Register & Pay on Venmo`
              : `Register ${cartCount} Class${cartCount > 1 ? 'es' : ''}`
          )}
        </button>
      </div>
    </Modal>
  );
};

export default CartModal;
